import * as _ from 'lodash';

import {
    GROUPS_LOAD,
    GROUPS_LOAD_SUCCESS,
    GROUP_ADD_SUCCESS,
    GROUP_DELETE,
    GROUP_DELETE_SUCCESS,
    GROUP_DUPLICATE_SUCCESS,
    GROUPS_UPDATE_SUCCESS,
    GROUP_LOAD,
    GROUP_LOAD_SUCCESS,
    GROUP_LOAD_ERROR,
    GROUP_DUPLICATE,
    GROUP_UPDATE_SUCCESS
} from './groups.constants';
import { Group, Identifier } from '../../models';
import { StoreItem } from '../Store';
import { IDENTIFIER_ADD_SUCCESS, IDENTIFIERS_SAVE_SUCCESS, IDENTIFIER_DELETE_SUCCESS, IDENTIFIER_DUPLICATE_SUCCESS, IDENTIFIERS_REORDER_SUCCESS, IDENTIFIERS_REORDER, IDENTIFIERS_REORDER_ERROR } from '../identifiers/identifiers.constants';
import { APPLICATION_DISCONNECTED } from '../application/application.constants';
import { copyStoreItem } from '../storeItemBuilder';

function deleteIdentifer(state: StoreItem<Group>, payload: { group: Group, guid: string }): StoreItem<Group> {
    const { group, guid } = payload;
    const newState = new StoreItem<Group>();
    for (const key of Object.keys(state)) {
        newState[key] = state[key];
    }
    group.identifierCollection = group.identifierCollection.filter(i => i.guid !== guid);
    newState.dictionary[group.guid] = group;
    return newState;
}

function buildGroupsStoreItem(payload: Group[]): StoreItem<Group> {
    const dic = {};
    payload.forEach(element => {
        dic[element.guid] = element;
    });
    return {
        dictionary: dic,
        isLoading: false,
        isLoaded: true,
        hasError: false,
        error: null
    };
}

export function groupsReducer(state: StoreItem<Group> = new StoreItem<Group>(), action: { type: string, payload: any }) {
    let newState = state;
    if (action && action.type) {
        const { payload } = action;
        const newDic = {};
        const group: Group = payload?.group;
        const identifier: Identifier = payload?.identifier;
        const identifiers: Identifier[] = payload?.identifiers;
        const isPartial: boolean = payload?.isPartial;
        let found = group && identifier
            ? _.find(group.identifierCollection, i => i.guid === identifier?.guid)
            : undefined;

        switch (action.type) {
            case GROUP_LOAD:
            case GROUPS_LOAD:
            case GROUP_DUPLICATE:
            case GROUP_DELETE:
                newState = {
                    dictionary: state.dictionary,
                    isLoading: true,
                    isLoaded: false,
                    hasError: false,
                    error: null
                };
                break;
            case GROUP_LOAD_SUCCESS:
                for (const key of Object.keys(state.dictionary)) {
                    newDic[key] = state.dictionary[key];
                }
                newDic[group.guid] = group;
                newState = {
                    dictionary: newDic,
                    isLoading: false,
                    isLoaded: true,
                    hasError: false,
                    error: null
                };
                break;
            case GROUP_LOAD_ERROR:
                newState = {
                    dictionary: state.dictionary,
                    isLoading: false,
                    isLoaded: false,
                    hasError: true,
                    error: action.payload
                };
                break;
            case GROUPS_LOAD_SUCCESS:
            case GROUP_DUPLICATE_SUCCESS:
                newState = buildGroupsStoreItem(payload);
                break;
            case GROUPS_UPDATE_SUCCESS:
                for (const key of Object.keys(state.dictionary)) {
                    newDic[key] = state.dictionary[key];
                }
                action.payload.forEach(element => {
                    newDic[element.guid] = element;
                });
                newState = {
                    dictionary: newDic,
                    isLoading: false,
                    isLoaded: true,
                    hasError: false,
                    error: null
                };
                break;
            case GROUP_ADD_SUCCESS:
            case GROUP_UPDATE_SUCCESS:
                if (action.payload) {
                    const newDic = {};
                    for (const key of Object.keys(state.dictionary)) {
                        newDic[key] = state.dictionary[key];
                    }
                    newDic[action.payload.guid] = action.payload;
                    newState = {
                        dictionary: newDic,
                        isLoading: false,
                        isLoaded: true,
                        hasError: false,
                        error: null
                    };
                }
                break;
            case IDENTIFIER_DUPLICATE_SUCCESS:
            case IDENTIFIER_ADD_SUCCESS:
                newState = new StoreItem<Group>();
                for (const key of Object.keys(state)) {
                    newState[key] = state[key];
                }
                if (found) {
                    found = identifier;
                } else {
                    group.identifierCollection.push(identifier);
                }
                newState.dictionary[group.guid] = group;
                break;
            case IDENTIFIERS_SAVE_SUCCESS:
                newState = new StoreItem<Group>();
                for (const key of Object.keys(state)) {
                    newState[key] = state[key];
                }
                if (identifiers && identifiers.length) {
                    if (group) {
                        newState.dictionary[group.guid] = remapIdentifiers(group, identifiers, isPartial);
                    } else {
                        const dic = {};
                        for (const key of Object.keys(newState.dictionary)) {
                            newState.dictionary[key].identifierCollection
                                .forEach(id => dic[id.guid] = key);
                        }
                        identifiers.forEach(id => {
                            const currentGroup = newState.dictionary[dic[id.guid]];
                            if (currentGroup) {
                                newState.dictionary[currentGroup.guid] = remapIdentifiers(currentGroup, [id], isPartial);
                            }
                        });
                    }
                }
                break;
            case IDENTIFIER_DELETE_SUCCESS:
                newState = deleteIdentifer(state, payload);
                break;
            case GROUP_DELETE_SUCCESS:
                newState = new StoreItem<Group>();
                for (const key of Object.keys(state)) {
                    newState[key] = state[key];
                }
                newState.isLoading = false;
                delete newState.dictionary[action.payload.guid];
                break;
            case IDENTIFIERS_REORDER:
                newState = copyStoreItem(state.dictionary);
                newState.isLoading = true;
                break;
            case IDENTIFIERS_REORDER_ERROR:
                newState = copyStoreItem(state.dictionary);
                newState.hasError = true;
                break;
            case IDENTIFIERS_REORDER_SUCCESS:
                newState = copyStoreItem(state.dictionary);
                newState.dictionary[group.guid].identifierCollection = identifiers;
                newState.isLoading = false;
                newState.hasError = false;
                break;
            case APPLICATION_DISCONNECTED:
                newState = new StoreItem<Group>();
                break;
        }
    }
    return newState;
}

function remapIdentifiers(group: Group, identifiers: Identifier[], isPartial: boolean = false): Group {
    const newIdentifiers = group.identifierCollection.filter((gi: any) => !gi.isCreating);
    if (isPartial) {
        identifiers.forEach(id => {
            const existing = newIdentifiers.filter(ni => ni.guid === id.guid);
            if (existing.length) {
                const index = newIdentifiers.indexOf(existing[0]);
                newIdentifiers[index] = id;
            }
        });
    } else {
        identifiers.forEach(id => {
            const existing = newIdentifiers.filter(ni => ni.guid === id.guid);
            if (existing.length) {
                const index = newIdentifiers.indexOf(existing[0]);
                newIdentifiers.splice(index);
            }
            newIdentifiers.push(id);
        });
    }
    group.identifierCollection = newIdentifiers;
    return group;
}